/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import {
  Button,
  BUTTON_FORMAT_TRANSPARENT,
  BUTTON_THEME_DEFAULT,
  ICON_ADD,
  SelectField,
  SelectFieldOption,
  TextField,
} from '@squiz/sds';
import {
  EntitySegmentEventSubscription,
  EVENT_RULE_LOGIC_OPERATOR_AND,
  EVENT_RULE_LOGIC_OPERATOR_OR,
  EventRuleSubscriptionPropertyValue,
} from 'apps/customer-data-platform/src/Services/segmentation';
import cloneDeep from 'lodash/cloneDeep';
import flatten from 'lodash/flatten';
import isArray from 'lodash/isArray';
import isObject from 'lodash/isObject';
import property from 'lodash/property';
import set from 'lodash/set';
import some from 'lodash/some';
import React, { SyntheticEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CDP_STRINGS } from '../../../../../../Constants/Strings.constants';
import { EVENT_BUILDER_DATA_IDS } from '../../../../EventBuilder.constants';
import { ALL_OPTIONS, comparisonOperatorSelectMap } from '../../EventBuilderWebBehaviorForm.constants';
import { EventBuilderWebBehaviorAttributesProps } from './EventBuilderWebBehaviorAttributes.props';

const ICON_REMOVE_CIRCLE = (
  <svg height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg">
    <path d="M7 12c0 .55.45 1 1 1h8c.55 0 1-.45 1-1s-.45-1-1-1H8c-.55 0-1 .45-1 1zm5-10C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"></path>
  </svg>
);

const KEY_VALUE_ATTRIBUTE_PREFIXES = [`idp`, `url.query`, `referrer.query`];

const ATTRIBUTE_VALUE_SEPARATOR = `.`;

const RULE_LOGIC_OPERATORS = [EVENT_RULE_LOGIC_OPERATOR_AND, EVENT_RULE_LOGIC_OPERATOR_OR].map(
  (operator): SelectFieldOption => {
    return {
      dataId: `rule-logic-operator-${operator}`,
      key: operator,
      label: operator,
      value: operator,
    };
  },
);

/**
 * Construct an event property key from a given property object.
 * @param {object} propertyObject A property object.
 * @param {Array<string>} parts Previous key parts.
 * @returns {string} Full key separated by `.` character.
 */
export function constructEventPropertyKey(propertyObject: object, parts: Array<string> = []): string | Array<unknown> {
  const keys = Object.keys(propertyObject);
  // Stop iterating if we've encountered more keys than expected or the property comparison operators.
  if (keys.includes(`comparison`)) {
    return parts.join(`.`);
  } else {
    return keys.map((key) => {
      return constructEventPropertyKey(property(key)(propertyObject) as object, [...parts, key]);
    });
  }
}

/**
 * Converts an event property from API data to a UI friendly attributes list.
 * @param {EventBuilderWebBehaviorAttributesProps['properties']} properties API property data.
 * @returns {EventBuilderWebBehaviorAttributesList} A list of converted attributes.
 */
export function convertEventPropertyToAttributesList(
  properties: EventBuilderWebBehaviorAttributesProps['properties'],
): EventBuilderWebBehaviorAttributesList {
  let propertiesToList: EntitySegmentEventSubscription['property'];
  // Convert old format of properties into new format (see PERSONIZE-1021).
  if (!Array.isArray(properties)) {
    const keys = flatten(flatten(constructEventPropertyKey(properties as object) as Array<string>));
    propertiesToList = keys.map((key): EventRuleSubscriptionPropertyValue => {
      const prop = property(key)(properties);
      return {
        ...(prop as Partial<EventRuleSubscriptionPropertyValue>),
        attribute: key,
      } as EventRuleSubscriptionPropertyValue;
    });
  } else {
    // At this point the properties passed should be in the correct format and require no conversion to read them.
    propertiesToList = properties as EntitySegmentEventSubscription['property'];
  }

  return propertiesToList.map((prop, index): EventBuilderWebBehaviorAttribute => {
    const { attribute, comparison, value } = prop;
    let keyValue;
    let name = attribute;

    // Key/value type attributes have a known prefix.
    KEY_VALUE_ATTRIBUTE_PREFIXES.forEach((prefix) => {
      // Check if the attribute starts with a certain prefix, then we know to break it into key/value pairs.
      if (attribute.startsWith(`${prefix}${ATTRIBUTE_VALUE_SEPARATOR}`)) {
        keyValue = attribute.replace(`${prefix}${ATTRIBUTE_VALUE_SEPARATOR}`, ``);
        name = prefix;
      }
    });

    const webBehaviourAttribute: EventBuilderWebBehaviorAttribute = {
      comparison: comparison as string,
      // Note: using an index here to match items in order of array. Items are no longer unique in terms of key.
      // They cannot be re-ordered so using an index here in terms of React is fine.
      id: `attr-${index}`,
      key: keyValue as string | undefined,
      name,
      value: (Array.isArray(value) ? value[0] : value) as string,
    };

    return webBehaviourAttribute;
  });
}

/**
 * The result of an attribute list conversion.
 */
interface convertAttributeListResult {
  errors: Record<string, string | null>;
  properties: EventBuilderWebBehaviorAttributesProps['properties'];
}

/**
 * Convert an attributes list to event property for data API.
 * @param {EventBuilderWebBehaviorAttributesList} attributes The list of attributes to convert.
 * @returns {convertAttributeListResult} Formatted event property data.
 */
export function convertAttributeListToEventProperty(
  attributes: EventBuilderWebBehaviorAttributesList,
): convertAttributeListResult {
  const errors: Record<string, string | null> = {};
  const properties = attributes
    .filter((attr) => {
      // Setup initial error state for names.
      errors[`name-${attr.id}`] = null;

      // Reset errors for other fields.
      [`comparison`, `value`, `key`].forEach((prefix) => {
        errors[`${prefix}-${attr.id}`] = null;
      });

      // Filter property values that do not have current errors.
      return errors[`name-${attr.id}`] === null;
    })
    .map((attr) => {
      let attribute = attr.name;
      if (attr.key) {
        attribute = `${attr.name}${ATTRIBUTE_VALUE_SEPARATOR}${attr.key}`;
      }
      return {
        attribute,
        comparison: attr.comparison,
        value: attr.value,
      };
    });

  return {
    errors,
    properties,
  };
}

/**
 * A single event builder web behavior attribute.
 */
export interface EventBuilderWebBehaviorAttribute {
  /**
   * The comparison operator.
   */
  comparison: string;
  /**
   * The id used to uniquely identify an attribute (may be a temporary id).
   */
  id: string;
  /**
   * An optional key value (valid for query param style attributes).
   */
  key?: string;
  /**
   * The key used in the 'attribute' select, such as `url.domain`.
   */
  name: string;
  /**
   * The value text field.
   */
  value: string;
}

/**
 * A list of Event builder web behavior attributes.
 */
export type EventBuilderWebBehaviorAttributesList = Array<EventBuilderWebBehaviorAttribute>;

/**
 * The event attributes for web behavior.
 * @param {EventBuilderWebBehaviorAttributesProps} props Component props.
 * @export
 * @returns {JSX.Element} The `<EventBuilderWebBehaviorForm />` component.
 */
export const EventBuilderWebBehaviorAttributes = (props: EventBuilderWebBehaviorAttributesProps): JSX.Element => {
  const {
    properties,
    setProperties,
    attributeSelectOptions = null,
    errors = {},
    operator,
    setOperator,
    type,
    ...rest
  } = props;
  const { t } = useTranslation();

  let propertiesLength = 0;
  if (isArray(properties)) {
    propertiesLength = properties.length;
  } else if (isObject(properties)) {
    propertiesLength = Object.keys(properties).length;
  }

  const initialAttributes: EventBuilderWebBehaviorAttributesList =
    useMemo((): EventBuilderWebBehaviorAttributesList => {
      const initTmpKey = `$tmp.${Math.random() * 1000}-${new Date().getSeconds()}`;
      return propertiesLength >= 1
        ? convertEventPropertyToAttributesList(properties)
        : [
            {
              comparison: ``,
              id: initTmpKey,
              name: initTmpKey,
              value: ``,
            },
          ];
    }, [properties, propertiesLength]);

  const [attributes, setAttributes] = useState<EventBuilderWebBehaviorAttributesList>(initialAttributes);

  // Each time the event type is changed make sure the initial state is reset.
  useEffect(() => {
    setAttributes(initialAttributes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  const labels = useMemo(() => {
    return {
      addAttribute: t(CDP_STRINGS.addAttribute),
      and: t(CDP_STRINGS.and),
      attribute: t(CDP_STRINGS.attribute),
      attributeFiltersDescription: t(CDP_STRINGS.attributeFiltersDescription),
      attributePlaceholder: t(CDP_STRINGS.attributeEnter),
      attributeTextfieldFrom: t(CDP_STRINGS.attributeTextfieldFrom),
      attributeTextfieldTo: t(CDP_STRINGS.attributeTextfieldTo),
      deleteFilter: t(CDP_STRINGS.deleteFilter),
      enterKeyPlaceholder: t(CDP_STRINGS.enterKeyPlaceholder),
      headerAttributeFilters: t(CDP_STRINGS.attributeFilters),
      rule: t(CDP_STRINGS.rule),
      ruleMatchingLogic: t(CDP_STRINGS.ruleOperatorLabel),
      selectRule: t(CDP_STRINGS.ruleSelectPlaceholder),
      value: t(CDP_STRINGS.valueFieldLabel),
      valueFieldPlaceholder: t(CDP_STRINGS.valueFieldPlaceholder),
    };
  }, [t]);

  const updateAttribute = useCallback(
    (index: number, key: string, value: unknown): void => {
      const nextAttributes = cloneDeep(attributes);
      set(nextAttributes[index], key, value);
      setAttributes(nextAttributes);

      // Convert attributes to event properties and pass them to the callback.
      const { properties, errors } = convertAttributeListToEventProperty(nextAttributes);
      setProperties(properties as EntitySegmentEventSubscription['property'], errors);
    },
    [attributes, setProperties],
  );

  const deleteAttribute = useCallback(
    (id: string): void => {
      const nextAttributes = attributes.filter((attribute) => attribute.id !== id);
      setAttributes(nextAttributes);

      // Note: setTimeout is used here to let the render cycle of this component to run before
      // updating the properties. Solves PERSONIZE-176.
      setTimeout(() => {
        // Convert attributes to event properties and pass them to the callback.
        const { properties, errors } = convertAttributeListToEventProperty(nextAttributes);
        setProperties(properties as EntitySegmentEventSubscription['property'], errors);
      }, 0);
    },
    [attributes, setProperties],
  );

  return (
    <div className="event-builder-web-behavior-form__attribute-filters" {...rest}>
      <h2 className="event-builder-web-behavior-form__attribute-filters-header">{labels.headerAttributeFilters}</h2>
      <div className="sds-row">
        <div className="event-builder-web-behavior-form__attributes-tableDescription">
          {labels.attributeFiltersDescription}
        </div>
        <div className="sds-row__body">
          <div className="sds-box">
            {attributes.map((attribute, index) => {
              const { name, id, comparison, value, key } = attribute;

              const comparisonOperators =
                (property(name)(comparisonOperatorSelectMap) as Array<SelectFieldOption>) || ALL_OPTIONS;
              let selectedComparison = comparison;

              // The following check sets the selected comparison to the first value IF there was
              // previously a value set in comparison and it doesn't match any of the existing options.
              if (
                comparison !== `` &&
                !comparisonOperators.filter((operator) => operator.value === comparison).length
              ) {
                selectedComparison = comparisonOperators[0].value;
              }

              let isKeyValue = false;
              if (some(KEY_VALUE_ATTRIBUTE_PREFIXES, (key) => name.startsWith(key))) {
                isKeyValue = true;
              }

              return (
                <>
                  <div className="event-builder-web-behavior-form__attributes-row" key={id}>
                    <div className="event-builder-web-behavior-form__attributes-row-item">
                      {attributeSelectOptions !== null && (
                        <SelectField
                          callback={(option): void => {
                            updateAttribute(index, `name`, option.value);
                          }}
                          defaultOption={
                            attributeSelectOptions
                              .filter((option) => {
                                const attrKey = name.split(ATTRIBUTE_VALUE_SEPARATOR).shift();
                                return option.key === attrKey;
                              })
                              .pop() || undefined
                          }
                          errorMessage={property(`name-${id}`)(errors) as string}
                          fieldLabel={labels.attribute}
                          id={`name-${id}`}
                          name={`name-${id}`}
                          options={attributeSelectOptions}
                          placeholder={`Select attribute`}
                          required
                          value={name.startsWith(`$tmp`) ? `` : name}
                        />
                      )}
                      {/* No attribute options will default to a textfield for entry */}
                      {attributeSelectOptions === null && (
                        <TextField
                          errorMessage={property(`name-${id}`)(errors) as string}
                          fieldLabel={labels.attribute}
                          id={`name-${id}`}
                          name={`name-${id}`}
                          onChange={(e): void => {
                            // @ts-ignore.
                            updateAttribute(index, `name`, e.target.value);
                          }}
                          placeholder={`Enter attribute`}
                          required
                          value={name.startsWith(`$tmp`) ? `` : name}
                        />
                      )}
                    </div>
                    <div className="event-builder-web-behavior-form__attributes-row-item">
                      <SelectField
                        callback={(option): void => {
                          updateAttribute(index, `comparison`, option.value);
                        }}
                        defaultOption={
                          comparisonOperators
                            .filter((option) => {
                              return option.value === selectedComparison;
                            })
                            .pop() || undefined
                        }
                        errorMessage={property(`comparison-${id}`)(errors) as string}
                        fieldLabel={labels.rule}
                        id={`comparison-${id}`}
                        name={`comparison-${id}`}
                        options={comparisonOperators}
                        placeholder={labels.selectRule}
                        required
                        value={selectedComparison}
                      />
                    </div>
                    <div className="event-builder-web-behavior-form__attributes-row-item">
                      {isKeyValue && (
                        <TextField
                          data-id={`key-${id}`}
                          errorMessage={property(`key-${id}`)(errors) as string}
                          fieldLabel={`Key`}
                          id={`key-${id}`}
                          name={`key-${id}`}
                          onChange={(e): void => {
                            updateAttribute(
                              index,
                              `key`,
                              (document.getElementById(`key-${id}`) as HTMLInputElement)?.value,
                            );
                          }}
                          placeholder={labels.enterKeyPlaceholder}
                          required
                          value={key ? key : ``}
                        />
                      )}
                      <TextField
                        data-id={`value-${id}`}
                        errorMessage={property(`value-${id}`)(errors) as string}
                        fieldLabel={labels.value}
                        id={`value-${id}`}
                        name={`value-${id}`}
                        onChange={(e): void => {
                          updateAttribute(
                            index,
                            `value`,
                            (document.getElementById(`value-${id}`) as HTMLInputElement)?.value,
                          );
                        }}
                        placeholder={labels.valueFieldPlaceholder}
                        required
                        value={value}
                      />
                    </div>
                    <div className="event-builder-web-behavior-form__attributes-row-item">
                      <Button
                        disabled={attributes.length === 1}
                        format={BUTTON_FORMAT_TRANSPARENT}
                        icon={ICON_REMOVE_CIRCLE}
                        label={`Delete filter`}
                        onClick={(e: SyntheticEvent): void => {
                          deleteAttribute(id);
                        }}
                        style={{ marginTop: `1.45rem` }}
                        theme={BUTTON_THEME_DEFAULT}
                      />
                    </div>
                  </div>
                  {index + 1 !== attributes.length && (
                    <div className="divider__rules">
                      <span>{operator.toUpperCase()}</span>
                    </div>
                  )}
                </>
              );
            })}
            <div className="event-builder-web-behavior-form__attribute-actions">
              <Button
                data-id={EVENT_BUILDER_DATA_IDS.eventBuilderAddAttribute}
                format={BUTTON_FORMAT_TRANSPARENT}
                icon={ICON_ADD}
                label={labels.addAttribute}
                onClick={(): void => {
                  const tmpKey = `$tmp.${Math.random() * 1000}-${new Date().getSeconds()}`;
                  setAttributes([
                    ...attributes,
                    {
                      comparison: ``,
                      id: tmpKey,
                      name: tmpKey,
                      value: ``,
                    },
                  ]);
                }}
                text={labels.addAttribute}
                theme={BUTTON_THEME_DEFAULT}
              />
              <div style={{ flex: 1 }}></div>
              <div className="event-builder-web-behavior-form__attribute-actions-rule">
                <SelectField
                  callback={(option): void => {
                    setOperator(option.value as EntitySegmentEventSubscription['operator']);
                  }}
                  data-id={`rule-matching-logic`}
                  defaultOption={
                    RULE_LOGIC_OPERATORS.filter((option) => {
                      return option.value === operator;
                    }).pop() || undefined
                  }
                  fieldLabel={labels.ruleMatchingLogic}
                  id={`rule-matching-logic`}
                  labelPosition="left"
                  name={`rule-matching-logic`}
                  options={RULE_LOGIC_OPERATORS}
                  placeholder={labels.selectRule}
                  value={operator}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
